import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { supabase } from "../supabase";

/**
 * Parse a monolithic ChordPro string into rough sections.
 */
const mapHeaderToCueKey = (label = "") => {
  const norm = label.toUpperCase();
  if (/\bINTRO\b/.test(norm)) return "intro";
  if (/\bALT VERSE\b/.test(norm) || /\bVERSE\b/.test(norm)) return "verse";
  if (/\bPRE ?CHORUS\b/.test(norm)) return "prechorus";
  if (/\bCHORUS\b/.test(norm)) return "chorus";
  if (/\bBRIDGE\b/.test(norm)) return "bridge";
  if (/\bTAG\b/.test(norm)) return "tag";
  if (/\bINSTRUMENTAL\b/.test(norm)) return "instrumental";
  if (/\bINTERLUDE\b/.test(norm)) return "interlude";
  if (/\bOUTRO\b/.test(norm)) return "outro";
  if (/\bENDING\b/.test(norm) || /\bEND\b/.test(norm)) return "ending";
  if (/^BREAK\b/.test(norm)) return "break";
  if (/^BUILD\b/.test(norm)) return "build";
  if (/^DROP\b/.test(norm)) return "drop";
  return "none";
};

export function parseChordProIntoSections(chordpro = "") {
  const lines = chordpro.split(/\r?\n/);
  const sections = [];
  let current = null;

  const isHeader = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return false;
    if (trimmed.length < 2 || trimmed.length > 40) return false;

    const lower = trimmed.toLowerCase();
    const directivePrefixes = ["{title", "{subtitle", "{key", "{time", "{tempo", "{duration", "{capo", "{instrument", "{artist"];
    if (directivePrefixes.some((p) => lower.startsWith(p))) return false;

    // ignore obvious chord lines or chordpro syntax
    if (/[\\[\\]{}]/.test(trimmed)) return false;
    if (/^([A-G][#b]?(maj|min|sus|add|dim|aug)?\d*\s*)+$/.test(trimmed)) return false;
    if (/^([A-G][#b]?\/[A-G][#b]?\s*)+$/.test(trimmed)) return false;
    if (/^capo(\s+\d+)?$/i.test(trimmed)) return false;
    if (/^\(.*\)$/.test(trimmed)) return false;

    const norm = trimmed.toUpperCase().replace(/[^A-Z0-9 ]/g, "");

    const patterns = [
      { key: "INTRO", regex: /^INTRO(\s|[:\-]|[0-9]|$)/ },
      { key: "VERSE", regex: /^VERSE(\s|[:\-]|[0-9]|$)/ },
      { key: "ALT VERSE", regex: /^ALT VERSE/ },
      { key: "PRECHORUS", regex: /^PRE ?CHORUS/ },
      { key: "CHORUS", regex: /^CHORUS(\s|[:\-]|[0-9]|$)/ },
      { key: "BRIDGE", regex: /^BRIDGE(\s|[:\-]|[0-9]|$)/ },
      { key: "TAG", regex: /^TAG(\s|[:\-]|[0-9]|$)/ },
      { key: "INSTRUMENTAL", regex: /^INSTRUMENTAL/ },
      { key: "INTERLUDE", regex: /^INTERLUDE/ },
      { key: "BREAK", regex: /^BREAK(\s|$)/ },
      { key: "BUILD", regex: /^BUILD(\s|$)/ },
      { key: "DROP", regex: /^DROP(\s|$)/ },
      { key: "OUTRO", regex: /^OUTRO(\s|$)/ },
      { key: "ENDING", regex: /^ENDING(\s|$)/ },
      { key: "END", regex: /^END(\s|$)/ },
    ];

    const matched = patterns.find((p) => p.regex.test(norm));
    if (!matched) return false;
    return matched;
  };

  const pushCurrent = () => {
    if (!current) return;
    sections.push({
      ...current,
      content: current.contentLines.join("\n"),
    });
  };

  lines.forEach((line) => {
    const headerMatch = isHeader(line);
    if (headerMatch) {
      pushCurrent();
      current = {
        section_label: line.trim(),
        contentLines: [],
        section_order: (sections.length + 1) * 10,
        measures: 4,
        beats: "",
        cue_key: mapHeaderToCueKey(line.trim()),
      };
      return;
    }

    if (current) {
      current.contentLines.push(line);
    }
  });

  pushCurrent();

  if (sections.length === 0) {
    return [
      {
        section_label: "Full Song",
        content: chordpro || "",
        section_order: 10,
        measures: 4,
        beats: "",
        cue_key: "section",
      },
    ];
  }

  return sections;
}

export async function loadSections(songId) {
  const { data, error } = await supabase.from("song_sections").select("*").eq("song_id", songId).order("section_order", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function saveSections(songId, sections) {
  return supabase.rpc("upsert_song_sections", {
    p_song_id: songId,
    p_sections: sections,
  });
}

export async function loadOrCreateDefaultArrangement(songId, bandId) {
  const { data, error } = await supabase.rpc("ensure_default_arrangement", {
    p_song_id: songId,
    p_band_id: bandId,
  });
  if (error) throw error;
  return data;
}

const parsePositiveNumber = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  return num;
};

const createClientId = () => `sec_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const withDefaults = (section, index) => ({
  clientId: section.clientId || createClientId(),
  id: section.id ?? null,
  section_label: section.section_label || "",
  content: section.content ?? "",
  section_order: section.section_order ?? (index + 1) * 10,
  measures: section.measures ?? (section.beats ? "" : 4),
  beats: section.beats ?? "",
  cue_key: section.cue_key || mapHeaderToCueKey(section.section_label),
  suppress_downbeat: Boolean(section.suppress_downbeat),
});

export default function SectionEditorModal({ songId, bandId, isOpen, onClose, onUpdated }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const canSave = useMemo(() => {
    if (!sections.length) return false;
    return sections.every((s) => {
      if (s.section_label.trim().length === 0) return false;
      const measures = parsePositiveNumber(s.measures);
      const beats = parsePositiveNumber(s.beats);
      return (measures || beats) && !(measures && beats);
    });
  }, [sections]);

  useEffect(() => {
    if (!isOpen || !songId) return;

    const init = async () => {
      setLoading(true);
      setError("");
      try {
        let loadedSections = await loadSections(songId);

        if (!loadedSections || loadedSections.length === 0) {
          // Auto-parse from chordpro but do NOT persist until user saves
          const { data: songData, error: songError } = await supabase.from("songs").select("chordpro").eq("id", songId).single();
          if (songError) throw songError;

          const parsed = parseChordProIntoSections(songData?.chordpro || "");
          loadedSections = parsed;
        }

        setSections(loadedSections.map(withDefaults));
      } catch (e) {
        console.error("Section editor load failed", e);
        setError(e.message || "Failed to load sections");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [isOpen, songId, bandId]);

  const updateSection = (idx, updates) => {
    setSections((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...updates };
      return next;
    });
  };

  const moveSection = (idx, direction) => {
    setSections((prev) => {
      const next = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const removeSection = (idx) => {
    setSections((prev) => prev.filter((_, i) => i !== idx));
  };

  const addSection = () => {
    setSections((prev) => [
      ...prev,
      {
        clientId: createClientId(),
        id: null,
        section_label: `Verse ${prev.length + 1}`,
        content: "",
        section_order: (prev.length + 1) * 10,
        measures: 4,
        beats: "",
        cue_key: mapHeaderToCueKey(`Verse ${prev.length + 1}`),
      },
    ]);
  };

  const handleSave = async () => {
    if (!canSave) {
      setError("Each section needs a title.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const cleaned = sections.map((s, idx) => ({
        id: s.id ?? null,
        section_label: s.section_label.trim(),
        content: s.content ?? "",
        section_order: (idx + 1) * 10,
        measures: parsePositiveNumber(s.beats) ? null : parsePositiveNumber(s.measures),
        beats: parsePositiveNumber(s.beats),
        cue_key: s.cue_key || "none",
        suppress_downbeat: Boolean(s.suppress_downbeat),
      }));

      if (cleaned.some((s) => !s.measures && !s.beats)) {
        setError("Each section needs measures or beats.");
        setSaving(false);
        return;
      }

      await saveSections(songId, cleaned);
      const arrangementId = await loadOrCreateDefaultArrangement(songId, bandId);
      await supabase.rpc("sync_arrangement_sections", { p_arrangement_id: arrangementId });

      if (onUpdated) onUpdated(cleaned);
      if (onClose) onClose();
    } catch (e) {
      console.error("Section save failed", e);
      setError(e.message || "Failed to save sections");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" fullscreen="md-down" className="arrange-modal">
      <Modal.Header closeButton className="arrange-modal-header">
        <Modal.Title className="mb-0">Section Editor</Modal.Title>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={!canSave || saving}
          className="ms-2"
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </Modal.Header>
      <Modal.Body className="arrange-modal-body">
        {loading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <>
            {error ? (
              <Alert variant="danger" onClose={() => setError("")} dismissible>
                {error}
              </Alert>
            ) : null}
            {sections.map((section, idx) => (
              <div key={section.clientId} className="border rounded p-3 mb-3">
                <div className="d-flex flex-wrap gap-2 align-items-end mb-2">
                  <div className="flex-grow-1">
                    <Form.Label className="mb-1">Label</Form.Label>
                    <Form.Control
                      value={section.section_label}
                      onChange={(e) => updateSection(idx, { section_label: e.target.value, cue_key: mapHeaderToCueKey(e.target.value) })}
                      required
                    />
                  </div>
                  <div style={{ width: "90px" }}>
                    <Form.Label className="mb-1">Measures</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={section.measures}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const next = raw === "" ? "" : Math.max(1, Number(raw));
                        updateSection(idx, { measures: next, beats: "" });
                      }}
                    />
                  </div>
                  <div
                    style={{
                      padding: "6px 8px",
                      border: "1px solid rgba(255,255,255,0.2)",
                      borderRadius: "6px",
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      lineHeight: 1.2,
                    }}
                  >
                    or beats
                  </div>
                  <div style={{ width: "90px" }}>
                    <Form.Label className="mb-1">Beats</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={section.beats}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const next = raw === "" ? "" : Math.max(1, Number(raw));
                        updateSection(idx, { beats: next, measures: "" });
                      }}
                    />
                  </div>
                  <div style={{ width: "140px" }}>
                    <Form.Label className="mb-1">Downbeat</Form.Label>
                    <Form.Check
                      type="checkbox"
                      label="Suppress"
                      checked={Boolean(section.suppress_downbeat)}
                      onChange={(e) => updateSection(idx, { suppress_downbeat: e.target.checked })}
                    />
                  </div>
                  <div style={{ width: "160px" }}>
                    <Form.Label className="mb-1">Cue Key</Form.Label>
                    <Form.Select value={section.cue_key} onChange={(e) => updateSection(idx, { cue_key: e.target.value })}>
                      {[
                        "none",
                        "verse",
                        "chorus",
                        "bridge",
                        "intro",
                        "break",
                        "build",
                        "drop",
                        "ending",
                        "tag",
                        "prechorus",
                        "instrumental",
                        "interlude",
                        "refrain",
                        "outro",
                        "section",
                      ].map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="ms-auto d-flex gap-2">
                    <Button variant="outline-secondary" size="sm" onClick={() => moveSection(idx, -1)} disabled={idx === 0}>
                      ↑
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => moveSection(idx, 1)} disabled={idx === sections.length - 1}>
                      ↓
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => removeSection(idx)}>
                      Delete
                    </Button>
                  </div>
                </div>
                <Form.Group className="mb-2">
                  <Form.Label>Content</Form.Label>
                  <Form.Control as="textarea" rows={4} value={section.content} onChange={(e) => updateSection(idx, { content: e.target.value })} />
                </Form.Group>
              </div>
            ))}
            <div className="d-flex justify-content-between align-items-center">
              <Button variant="outline-success" onClick={addSection}>
                + Add Section
              </Button>
              <div className="d-flex gap-2">
                <Button variant="secondary" onClick={onClose} disabled={saving}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={!canSave || saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
